// gallery.js

class SP_Gallery {
    constructor(elementId, images, options = {}) {
        this.galleryElement = document.getElementById(elementId);
        if (!this.galleryElement) {
            console.error(`SP_Gallery Error: Element with ID "${elementId}" not found.`);
            return;
        }

        this.images = images || [];
        this.currentIndex = 0;
        this.touchStartX = 0;

        // --- Default Options ---
        const defaults = {
            // General
            click: 1,
            // Responsive Widths
            desktop_width: '90vw',
            tablet_width: '95%',
            mobile_width: '100%',
            // Responsive Columns
            desktop_columns: 4,
            tablet_columns: 3,
            mobile_columns: 1,
            // Image Sizing
            img_width: null,
            img_height: null,
            img_square: 0,
            img_square_fit: 0,
        };
        this.options = { ...defaults, ...options };

        this.initialize();
    }

    /**
     * Builds the gallery grid and sets up the modal if needed.
     */
    initialize() {
        this.galleryElement.innerHTML = '';

        if (this.images.length === 0) {
            this.galleryElement.innerHTML = '<p>No images to display.</p>';
            return;
        }
        
        this.galleryElement.classList.add('sp-gallery-grid');
        
        // --- Set CSS variables for responsive widths ---
        this.galleryElement.style.setProperty('--desktop-width', this.options.desktop_width);
        this.galleryElement.style.setProperty('--tablet-width', this.options.tablet_width);
        this.galleryElement.style.setProperty('--mobile-width', this.options.mobile_width);
        
        // --- Set CSS variables for responsive columns (with validation) ---
        const desktopCols = this.options.desktop_columns;
        const tabletCols = Math.min(this.options.tablet_columns, 5); // Max 5 for tablet
        const mobileCols = Math.min(this.options.mobile_columns, 3); // Max 3 for mobile
        this.galleryElement.style.setProperty('--desktop-cols', desktopCols);
        this.galleryElement.style.setProperty('--tablet-cols', tabletCols);
        this.galleryElement.style.setProperty('--mobile-cols', mobileCols);
        
        // --- Apply image sizing classes/styles ---
        if (this.options.img_square) this.galleryElement.classList.add('sp-img-square-cover');
        else if (this.options.img_square_fit) this.galleryElement.classList.add('sp-img-square-contain');
        else if (this.options.img_width) {
            this.galleryElement.classList.add('sp-img-fixed-width');
            this.galleryElement.style.setProperty('--sp-img-width', this.options.img_width);
        } else if (this.options.img_height) {
            this.galleryElement.classList.add('sp-img-fixed-height');
            this.galleryElement.style.setProperty('--sp-img-height', this.options.img_height);
        }

        // --- Create and append each image item ---
        this.images.forEach((base64, index) => {
            const item = document.createElement('div');
            item.className = 'sp-gallery-item';
            const img = document.createElement('img');
            img.src = base64;
            img.alt = `Gallery image ${index + 1}`;
            img.loading = 'lazy';
            item.appendChild(img);
            if (this.options.click) {
                item.addEventListener('click', () => this.openModal(index));
            }
            this.galleryElement.appendChild(item);
        });

        if (this.options.click) {
            this.createModal();
        }
    }

    /**
     * Creates the modal DOM elements and appends them to the body.
     */
    createModal() {
        if (document.querySelector('.sp-modal-overlay')) return;
        const modalHTML = `
            <div class="sp-modal-overlay">
                <button class="sp-modal-close" title="Close">&times;</button>
                <button class="sp-modal-nav prev" title="Previous">&#10094;</button>
                <div class="sp-modal-content"><img src="" class="sp-modal-image" alt="Enlarged view"></div>
                <button class="sp-modal-nav next" title="Next">&#10095;</button>
            </div>`;
        document.body.insertAdjacentHTML('beforeend', modalHTML);
        this.modal = document.querySelector('.sp-modal-overlay');
        this.modalImage = this.modal.querySelector('.sp-modal-image');
        this.modal.querySelector('.sp-modal-close').addEventListener('click', () => this.closeModal());
        this.modal.querySelector('.prev').addEventListener('click', () => this.prevImage());
        this.modal.querySelector('.next').addEventListener('click', () => this.nextImage());
        this.modal.addEventListener('click', (e) => { if (e.target === this.modal) this.closeModal(); });
        this.addNavigationHandlers();
    }

    openModal(index) { 
        this.currentIndex = index; 
        this.showImage(this.currentIndex); 
        this.modal.style.display = 'flex'; 
        document.body.style.overflow = 'hidden'; 
    }
    
    closeModal() { 
        this.modal.style.display = 'none'; 
        document.body.style.overflow = ''; 
    }
    
    showImage(index) { 
        if (index < 0 || index >= this.images.length) return; 
        this.modalImage.src = this.images[index]; 
        this.currentIndex = index; 
    }
    
    nextImage() { 
        this.showImage((this.currentIndex + 1) % this.images.length); 
    }
    
    prevImage() { 
        this.showImage((this.currentIndex - 1 + this.images.length) % this.images.length); 
    }
    
    addNavigationHandlers() {
        document.addEventListener('keydown', (e) => { 
            if (this.modal.style.display !== 'flex') return; 
            if (e.key === 'ArrowRight') this.nextImage(); 
            if (e.key === 'ArrowLeft') this.prevImage(); 
            if (e.key === 'Escape') this.closeModal(); 
        });
        
        this.modal.addEventListener('touchstart', (e) => { 
            this.touchStartX = e.changedTouches[0].screenX; 
        }, { passive: true });
        
        this.modal.addEventListener('touchend', (e) => { 
            const touchEndX = e.changedTouches[0].screenX; 
            const swipeDistance = touchEndX - this.touchStartX; 
            if (swipeDistance > 50) this.prevImage(); 
            else if (swipeDistance < -50) this.nextImage(); 
        });
    }
}